/** Indian-format currency and number helpers used across the app. */

export function formatINR(value: number, opts: { compact?: boolean; decimals?: boolean } = {}): string {
  if (!Number.isFinite(value)) return "₹0";
  if (opts.compact) {
    const abs = Math.abs(value);
    if (abs >= 10000000) return `₹${trim(value / 10000000)}Cr`;
    if (abs >= 100000) return `₹${trim(value / 100000)}L`;
    if (abs >= 1000) return `₹${trim(value / 1000)}K`;
  }
  return value.toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: opts.decimals ? 2 : 0,
    maximumFractionDigits: opts.decimals ? 2 : 0,
  });
}

function trim(n: number): string {
  return n.toFixed(Math.abs(n) >= 100 ? 0 : 1).replace(/\.0$/, "");
}

export function formatPoints(value: number): string {
  // formatINR already guards this. A missing number reaches here whenever the
  // running API is older than the bundle asking it — worth showing as 0 rather
  // than as NaN, which reads like a broken balance.
  if (!Number.isFinite(value)) return "0";
  return Math.round(value).toLocaleString("en-IN");
}

export function formatPct(value: number, decimals = 2): string {
  return `${value.toFixed(decimals)}%`;
}

export function formatDate(value: string | Date): string {
  const d = typeof value === "string" ? new Date(value) : value;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

export function formatDateShort(value: string | Date): string {
  const d = typeof value === "string" ? new Date(value) : value;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
}

export function toDateInput(value: string | Date): string {
  const d = typeof value === "string" ? new Date(value) : value;
  const offset = d.getTimezoneOffset();
  return new Date(d.getTime() - offset * 60000).toISOString().slice(0, 10);
}

export function todayInput(): string {
  return toDateInput(new Date());
}

/** Fixed categorical series order — matches the validated palette. */
export const SERIES_COLORS = [
  "#2a78d6", "#eb6834", "#1baf7a", "#eda100",
  "#e87ba4", "#008300", "#4a3aa7", "#e34948",
];

export function seriesColor(index: number): string {
  return SERIES_COLORS[index % SERIES_COLORS.length];
}

/**
 * How to show a login. An account made from a mobile number is stored as
 * `9876543210@mobile.invalid`; nobody should see that, so the number is shown
 * instead. Mirrors describeLogin in server/src/lib/login.ts.
 */
export function describeLogin(storedEmail: string): string {
  const mobile = /^(?:91)?([6-9]\d{9})@/.exec(storedEmail)?.[1];
  return mobile ? `+91 ${mobile.slice(0, 5)} ${mobile.slice(5)}` : storedEmail;
}
